"use client";

import { useEffect, useState } from "react";
import { subscribeAddressMarksUpdates } from "@/lib/address-marks/storage";
import { searchAddressMarksInStorage } from "@/lib/search/address-mark-search";
import {
	analyzeSearchQuery,
	mergeUnifiedSearchWithAddressMarks,
	type UnifiedSearchState,
	performUnifiedSearch,
} from "@/lib/search/unified-search";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("hooks/useUnifiedSearch.ts");

interface UseUnifiedSearchOptions {
	enabled?: boolean;
	debounceMs?: number;
	labels?: {
		unsupportedEvm?: string;
		btcNotFound?: string;
		failed?: string;
	};
}

const EMPTY_STATE: UnifiedSearchState = {
	analysis: analyzeSearchQuery(""),
	exactToken: null,
	tokenResults: [],
	addressResult: null,
	addressMarkResults: [],
	directMatch: null,
	errorMessage: "",
};

export function useUnifiedSearch(
	query: string,
	options?: UseUnifiedSearchOptions,
) {
	const enabled = options?.enabled ?? true;
	const debounceMs = options?.debounceMs ?? 220;
	const [state, setState] = useState<UnifiedSearchState>(EMPTY_STATE);
	const [loading, setLoading] = useState(false);
	const [addressMarksVersion, setAddressMarksVersion] = useState(0);

	useEffect(() => {
		return subscribeAddressMarksUpdates(() => {
			setAddressMarksVersion((current) => current + 1);
		});
	}, []);

	useEffect(() => {
		if (!enabled) return;

		const timer = window.setTimeout(() => {
			const run = async () => {
				setLoading(true);
				try {
					const addressMarkResults = searchAddressMarksInStorage(query);
					const result = await performUnifiedSearch(query, options?.labels);
					setState(
						mergeUnifiedSearchWithAddressMarks(result, addressMarkResults),
					);
				} catch (error) {
					uiLog.error("Unified search failed:", error);
					const addressMarkResults = searchAddressMarksInStorage(query);
					setState(
						mergeUnifiedSearchWithAddressMarks(
							{
								...EMPTY_STATE,
								analysis: analyzeSearchQuery(query),
								errorMessage:
									options?.labels?.failed || "搜索失败，请稍后重试。",
							},
							addressMarkResults,
						),
					);
				} finally {
					setLoading(false);
				}
			};

			void run();
		}, debounceMs);

		return () => {
			window.clearTimeout(timer);
		};
	}, [addressMarksVersion, debounceMs, enabled, options?.labels, query]);

	return {
		...state,
		loading,
	};
}
